import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { useCart } from "../hooks/useCart";

const MAX_POLLS = 30;   // ~1 minute at 2s intervals
const POLL_INTERVAL_MS = 2000;

const gbp = (v) => `£${(Number(v) || 0).toFixed(2)}`;

/**
 * Post-payment landing page. Polls
 * `/ink-shop/checkout/status/{session_id}` until the transaction shows
 * `payment_status=paid` (or we hit the poll cap, in which case we
 * surface a "still processing" message with a retry link).
 *
 * Empties the localStorage cart once payment is confirmed so a page
 * reload doesn't leave the customer with a stale basket.
 */
export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [txn, setTxn] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState("");
  const { clear } = useCart();
  const cartCleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError("Missing session id — can't confirm payment. Contact us and quote your Stripe receipt.");
      return undefined;
    }
    let alive = true;
    let pollCount = 0;
    const tick = async () => {
      if (!alive) return;
      pollCount += 1;
      try {
        const r = await api.get(`/ink-shop/checkout/status/${sessionId}`);
        if (!alive) return;
        setTxn(r.data);
        setAttempts(pollCount);
        if (r.data.payment_status === "paid") {
          if (!cartCleared.current) {
            clear();
            cartCleared.current = true;
          }
          return; // done, stop polling
        }
        if (r.data.status === "expired") {
          setError("This checkout session expired before payment completed.");
          return;
        }
        if (pollCount >= MAX_POLLS) {
          setError(
            "We haven't received payment confirmation from Stripe yet. Refresh the page in a moment — if it still doesn't clear, contact us with your Stripe receipt.",
          );
          return;
        }
        window.setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (!alive) return;
        setError(
          e?.response?.data?.detail ||
          "Couldn't reach the server to confirm payment. Refresh in a moment.",
        );
      }
    };
    tick();
    return () => { alive = false; };
  }, [sessionId, clear]);

  const paid = txn?.payment_status === "paid";

  return (
    <div className="min-h-screen bg-[var(--brand-bg,#F5F7FA)] text-[var(--brand-ink)]" data-testid="checkout-thanks">
      <Header />
      <main className="mx-auto max-w-xl px-4 py-16">
        {error ? (
          <div className="rounded-md border border-red-200 bg-white p-8 text-center" data-testid="checkout-thanks-error">
            <XCircle className="mx-auto h-8 w-8 text-red-600" />
            <p className="mt-3 font-display text-lg font-bold">Payment status unclear</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">{error}</p>
            <Link to="/cart" className="mt-4 inline-block text-sm underline">Back to your cart</Link>
          </div>
        ) : !paid ? (
          <div className="rounded-md border border-black/10 bg-white p-8 text-center" data-testid="checkout-thanks-pending">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-[var(--brand-body)]" />
            <p className="mt-3 font-display text-lg font-bold">Confirming your payment…</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Hang on — this normally takes a couple of seconds. Attempt {attempts}/{MAX_POLLS}.
            </p>
          </div>
        ) : (
          <div className="rounded-md border border-emerald-200 bg-white p-8" data-testid="checkout-thanks-paid">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h1 className="mt-3 text-center font-display text-2xl font-black">Thank you!</h1>
            <p className="mt-2 text-center text-sm text-[var(--brand-body)]">
              Your payment was received and your order is on its way to our print
              partner in Blackburn. You&rsquo;ll get a confirmation email shortly, and
              another when it ships.
            </p>

            <ul className="mt-6 space-y-3" data-testid="checkout-thanks-lines">
              {(txn.line_summary || []).map((l, i) => (
                <li key={i} className="flex items-center gap-3 rounded-md border border-black/10 p-3">
                  {l.cover_image ? (
                    <img src={l.cover_image} alt="" className="h-14 w-14 rounded-md object-cover" />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-sm font-bold">{l.title}</p>
                    <p className="text-xs text-[var(--brand-body)]">{l.variant_label} · Qty {l.quantity}</p>
                  </div>
                  <p className="font-display text-sm font-bold">
                    {gbp((l.unit_price_gbp || 0) * (l.quantity || 1))}
                  </p>
                </li>
              ))}
            </ul>

            <div className="mt-6 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{gbp(txn.subtotal_gbp)}</span></div>
              <div className="flex justify-between"><span>Shipping · {txn.shipping_label}</span><span>{gbp(txn.shipping_gbp)}</span></div>
              <div className="flex justify-between border-t border-black/10 pt-2 font-display text-base font-bold">
                <span>Paid</span><span data-testid="checkout-thanks-total">{gbp(txn.amount_gbp)}</span>
              </div>
            </div>

            <div className="mt-6 flex justify-center gap-3">
              <Link to="/shop" className="rounded-md border border-black/15 px-4 py-2 font-display text-xs font-semibold hover:border-[#0A0F17]">
                Keep shopping
              </Link>
              <Link to="/" className="rounded-md bg-[#0A0F17] px-4 py-2 font-display text-xs font-semibold text-white">
                Back home
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
